import moment from 'moment'

// Field catalog for step 1 of the Transfer Incident Data modal. Each entry pairs the
// backend column name — what the mutation's field_overrides expects — with the camelCase
// GraphQL field and enough type information to compare and render the two values.
// The backend counterpart is MERGEABLE_INCIDENT_FIELDS in incidents/merge_fields.py.

export const SOURCE = "SOURCE"
export const DESTINATION = "DESTINATION"

// car_condition is intentionally absent: the damages-driven auto-assign in
// TransferIncidentDataMutation stays its only writer.
export const MERGE_FIELDS = [
    { key: "incident_time", gqlKey: "incidentTime", label: "Incident Time", type: "time" },
    { key: "notification_date", gqlKey: "notificationDate", label: "Notification Date", type: "datetime" },
    { key: "description", gqlKey: "description", label: "Description", type: "text", long: true },
    { key: "reported_by", gqlKey: "reportedBy", label: "Reported By", type: "enum", choices: "reportedByChoices" },
    { key: "branch", gqlKey: "branch", label: "Branch", type: "branch" },
    { key: "rental", gqlKey: "rental", label: "Rental", type: "rental" },
    { key: "incident_type", gqlKey: "incidentType", label: "Incident Type", type: "enum", choices: "incidentTypeChoices" },
    { key: "status", gqlKey: "status", label: "Status", type: "enum", choices: "statusChoices" },
    { key: "source", gqlKey: "source", label: "Source", type: "enum", choices: "sourceChoices" },
    { key: "responsible_party", gqlKey: "responsibleParty", label: "Responsible Party", type: "enum", choices: "responsiblePartyChoices" },
    { key: "has_liability_claim", gqlKey: "hasLiabilityClaim", label: "Has Liability Claim", type: "yesNo" },
    { key: "has_subro_claim", gqlKey: "hasSubroClaim", label: "Has Subro Claim", type: "yesNo" },
    { key: "has_police_report", gqlKey: "hasPoliceReport", label: "Has Police Report", type: "yesNo" },
    { key: "has_dashcam_videos", gqlKey: "hasDashcamVideos", label: "Has Dashcam Videos", type: "yesNo" },
    { key: "has_other_parties", gqlKey: "hasOtherParties", label: "Has Other Parties", type: "yesNo" },
    { key: "car_on_hold", gqlKey: "carOnHold", label: "Car On Hold", type: "boolean" },
    { key: "car_totaled", gqlKey: "carTotaled", label: "Car Totaled", type: "yesNo" },
    { key: "rideshare_platform", gqlKey: "ridesharePlatform", label: "Rideshare Platform", type: "enum", choices: "ridesharePlatformChoices" },
    { key: "rideshare_period", gqlKey: "ridesharePeriod", label: "Rideshare Period", type: "enum", choices: "ridesharePeriodChoices" },
    { key: "rideshare_period_source", gqlKey: "ridesharePeriodSource", label: "Rideshare Period Source", type: "enum", choices: "ridesharePeriodSourceChoices" },
    { key: "incident_state", gqlKey: "incidentState", label: "Incident State", type: "text" },
    { key: "location", gqlKey: "location", label: "Location", type: "text" },
    { key: "is_record_only", gqlKey: "isRecordOnly", label: "Record Only", type: "boolean" },
    { key: "notes", gqlKey: "notes", label: "Notes", type: "text", long: true },
]

// Enum labels come from schema introspection, matching the pattern already used in
// OverviewTab.js and IncidentsList.js. The has_* and car_totaled columns are plain
// YES/NO, so they need no introspection.
export const ENUM_CHOICE_TYPES = [
    { alias: "reportedByChoices", typeName: "IncidentsIncidentReportedByChoices" },
    { alias: "incidentTypeChoices", typeName: "IncidentsIncidentIncidentTypeChoices" },
    { alias: "statusChoices", typeName: "IncidentsIncidentStatusChoices" },
    { alias: "sourceChoices", typeName: "IncidentsIncidentSourceChoices" },
    { alias: "responsiblePartyChoices", typeName: "IncidentsIncidentResponsiblePartyChoices" },
    { alias: "ridesharePlatformChoices", typeName: "IncidentsIncidentRidesharePlatformChoices" },
    { alias: "ridesharePeriodChoices", typeName: "IncidentsIncidentRidesharePeriodChoices" },
    { alias: "ridesharePeriodSourceChoices", typeName: "IncidentsIncidentRidesharePeriodSourceChoices" },
]

// The selection set for one incident's merge fields, shared by both aliases in the query.
export const MERGE_FIELD_GQL = `
    id
    pk
    title
    incidentDate
    incidentTime
    notificationDate
    description
    reportedBy
    branch { id name }
    rental { id pk startDate }
    incidentType
    status
    source
    responsibleParty
    hasLiabilityClaim
    hasSubroClaim
    hasPoliceReport
    hasDashcamVideos
    hasOtherParties
    carOnHold
    carTotaled
    ridesharePlatform
    ridesharePeriod
    ridesharePeriodSource
    incidentState
    location
    isRecordOnly
    metadata
    notes
`

// metadata keys a merge may offer. crm_notes belongs to the step 2 checkbox and
// archive_reason describes an archival rather than a merge.
const MERGEABLE_METADATA_KEYS = [
    { key: "rideshare_incident_id", label: "Rideshare Incident ID" },
    { key: "crm_name", label: "CRM Name" },
    { key: "monday_row_title", label: "Monday Row Title" },
]

export function parseMetadata(metadata) {
    if (!metadata) return {}
    if (typeof metadata !== "string") return metadata
    try {
        return JSON.parse(metadata) || {}
    } catch {
        return {}
    }
}

function normalize(value) {
    if (value === undefined || value === null) return null
    if (typeof value === "string" && value.trim() === "") return null
    return value
}

export function readFieldValue(incident, field) {
    if (!incident || !field) return null
    if (field.type === "branch") return normalize(incident.branch?.id)
    if (field.type === "rental") return normalize(incident.rental?.id)
    return normalize(incident[field.gqlKey])
}

export function formatFieldValue(incident, field, choiceMaps = {}) {
    const value = readFieldValue(incident, field)
    if (value === null) return null

    switch (field.type) {
        case "time":
            return moment(value, "HH:mm:ss").format("h:mm A")
        case "datetime":
            return moment(value).format("MM/DD/YYYY h:mm A")
        case "boolean":
            return value ? "Yes" : "No"
        case "yesNo":
            return value === "YES" ? "Yes" : "No"
        case "enum": {
            const choices = choiceMaps[field.choices]?.enumValues || []
            return choices.find((item) => item.name === value)?.description || String(value)
        }
        case "branch":
            return incident.branch?.name || `#${value}`
        case "rental": {
            const startDate = incident.rental?.startDate
            const started = startDate ? ` · started ${moment(startDate).format("MM/DD/YYYY")}` : ""
            return `#${incident.rental?.pk ?? value}${started}`
        }
        default:
            return String(value)
    }
}

function buildRow(key, label, fromValue, toValue) {
    return {
        key,
        label,
        long: false,
        fromValue,
        toValue,
        fromDisplay: fromValue === null ? null : String(fromValue),
        toDisplay: toValue === null ? null : String(toValue),
    }
}

function metadataRows(fromIncident, toIncident) {
    const fromMeta = parseMetadata(fromIncident?.metadata)
    const toMeta = parseMetadata(toIncident?.metadata)
    const rows = []

    const systems = Array.from(
        new Set([
            ...Object.keys(fromMeta.external_ids || {}),
            ...Object.keys(toMeta.external_ids || {}),
        ])
    ).sort()

    systems.forEach((system) => {
        rows.push(
            buildRow(
                `metadata.external_ids.${system}`,
                `${system} ID`,
                normalize((fromMeta.external_ids || {})[system]),
                normalize((toMeta.external_ids || {})[system])
            )
        )
    })

    MERGEABLE_METADATA_KEYS.forEach(({ key, label }) => {
        rows.push(buildRow(`metadata.${key}`, label, normalize(fromMeta[key]), normalize(toMeta[key])))
    })

    return rows
}

export function diffIncidents(fromIncident, toIncident, choiceMaps = {}) {
    const differing = []
    const identical = []

    MERGE_FIELDS.forEach((field) => {
        const fromValue = readFieldValue(fromIncident, field)
        const toValue = readFieldValue(toIncident, field)
        const row = {
            key: field.key,
            label: field.label,
            long: !!field.long,
            fromValue,
            toValue,
            fromDisplay: formatFieldValue(fromIncident, field, choiceMaps),
            toDisplay: formatFieldValue(toIncident, field, choiceMaps),
        }
        if (fromValue === toValue) identical.push(row)
        else differing.push(row)
    })

    metadataRows(fromIncident, toIncident).forEach((row) => {
        if (row.fromValue === row.toValue) identical.push(row)
        else differing.push(row)
    })

    return { differing, identical }
}

export function defaultResolutions(rows) {
    return (rows || []).reduce((acc, row) => ({ ...acc, [row.key]: DESTINATION }), {})
}

export function buildFieldOverrides(resolutions) {
    return Object.keys(resolutions || {})
        .filter((key) => resolutions[key] === SOURCE)
        .map((field) => ({ field }))
}

// Taking the source's time on a shared date makes the two records collide on
// (car, incident_date, incident_time). That is allowed mid-merge, but worth flagging.
export function hasTimeCollision(fromIncident, toIncident, resolutions) {
    if ((resolutions || {}).incident_time !== SOURCE) return false
    if (!fromIncident?.incidentDate || !toIncident?.incidentDate) return false
    return fromIncident.incidentDate === toIncident.incidentDate
}
